// Bingo Bango Bongo runner.
//
// Each hole awards three points — Bingo (first on green), Bango (closest once
// all on), Bongo (first in the cup) — to the players picked live during the
// round (see BingoSelectionModal). Settlement is the same per-opponent transfer
// model as Putting: every point a player wins collects `betPerPoint` from each
// opponent, so in a fourball at $1 a Bingo is +$3 to the winner and −$1 to each
// of the three others. Zero-sum across all participants.
//
// Per-category buckets carry the player's OWN win count + holes (for the
// click-to-expand list) and their NET money for that category (own wins minus
// what they paid out on others' wins of the same category). The round total is
// the sum of the three category nets. Pure function — no React, no state.

import { Player } from "../types";
import { BingoSelections } from "../activeRound";

export type BbbCategory = "bingo" | "bango" | "bongo";
const CATEGORIES: BbbCategory[] = ["bingo", "bango", "bongo"];

export interface BingoBangoBongoOptions {
  betPerPoint?: number;
}

export interface BbbBucket {
  // This player's own wins in the category.
  count: number;
  // 1-based hole numbers where this player won the category.
  holes: number[];
  // Net money for this player in this category (own wins − losses to others').
  winnings: number;
}

export interface BbbPayout {
  total: number;
  bingo: BbbBucket;
  bango: BbbBucket;
  bongo: BbbBucket;
}

export interface BingoBangoBongoResult {
  payouts: Record<string, BbbPayout>;
  hasAnyBet: boolean;
}

export interface BingoBangoBongoInput {
  players: Player[];
  selections: BingoSelections;
  options: BingoBangoBongoOptions;
}

function emptyBucket(): BbbBucket {
  return { count: 0, holes: [], winnings: 0 };
}

export function runBingoBangoBongo(
  input: BingoBangoBongoInput,
): BingoBangoBongoResult {
  const { players, selections, options } = input;
  const bet = Number(options.betPerPoint) || 0;

  const payouts: Record<string, BbbPayout> = {};
  for (const p of players) {
    payouts[p.id] = {
      total: 0,
      bingo: emptyBucket(),
      bango: emptyBucket(),
      bongo: emptyBucket(),
    };
  }

  if (bet <= 0 || players.length < 2) {
    return { payouts, hasAnyBet: false };
  }

  for (const [holeStr, sel] of Object.entries(selections)) {
    if (!sel) continue;
    const hole = parseInt(holeStr, 10);
    for (const cat of CATEGORIES) {
      const winnerId = sel[cat];
      if (!winnerId || !payouts[winnerId]) continue;
      const opponents = players.filter((p) => p.id !== winnerId);
      if (opponents.length === 0) continue;

      const bucket = payouts[winnerId][cat];
      bucket.count += 1;
      bucket.holes.push(hole);
      bucket.winnings += bet * opponents.length;
      for (const op of opponents) {
        payouts[op.id][cat].winnings -= bet;
      }
    }
  }

  for (const p of players) {
    const pay = payouts[p.id];
    pay.bingo.holes.sort((a, b) => a - b);
    pay.bango.holes.sort((a, b) => a - b);
    pay.bongo.holes.sort((a, b) => a - b);
    pay.total =
      pay.bingo.winnings + pay.bango.winnings + pay.bongo.winnings;
  }

  return { payouts, hasAnyBet: true };
}
